import { useNavigation } from "@-/com.npmjs.expo-router";
import { CommittedPrimaryNavigationDestinationNameRefContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";
import { useContext, useEffect, useRef, useState, type RefObject } from "react";

type NavigationLike = {
  addListener: (event: "blur" | "focus", callback: () => void) => () => void;
  getParent: () => NavigationLike | undefined;
  getState: () =>
    | {
        index?: number;
        routeNames?: readonly string[];
        routes?: readonly { name: string }[];
        type?: string;
      }
    | undefined;
  isFocused: () => boolean;
};

/**
 * Which blurs count.
 * - `app`: any of them — "the learner can see this route right now".
 * - `bottom-tab`: only a blur that happens while Browse is still the front bottom tab, i.e. another
 *   route INSIDE Browse took over. A blur caused by switching bottom tabs is ignored and the last
 *   value is held — "this is still the route Browse is showing".
 *
 * `navigation.isFocused()` cannot tell the two apart: React Navigation answers it recursively
 * through every parent, so it turns false for the whole Browse tree the moment the tab blurs.
 */
export type NavigationTreeFocusScope = "app" | "bottom-tab";

function isNavigationTreeFocused(navigation: NavigationLike): boolean {
  for (
    let current: NavigationLike | undefined = navigation;
    current != null;
    current = current.getParent()
  ) {
    if (!current.isFocused()) {
      return false;
    }
  }
  return true;
}

/**
 * Whether Browse is the front bottom tab, from the tab navigator's own store (reduced
 * synchronously on every dispatch, so it is current even inside a blur event). True when no such
 * navigator is found, so the hook degrades to plain tree focus outside the app shell.
 */
function isBrowseFrontPrimaryNavigationDestination(navigation: NavigationLike): boolean {
  for (
    let current: NavigationLike | undefined = navigation;
    current != null;
    current = current.getParent()
  ) {
    const state = current.getState();
    if (state?.type === "tab" && state.routeNames?.includes("browse") === true) {
      return state.routes?.[state.index ?? 0]?.name === "browse";
    }
  }
  return true;
}

/**
 * True when this route and every ancestor navigator is focused. Nested navigator
 * layouts (e.g. Cards material top tabs inside the bottom-tab browse stack) are not
 * leaf screens — `useIsFocused()` on the layout alone can stay true after another
 * bottom tab takes focus while `freezeOnBlur` keeps the subtree mounted.
 */
export function useNavigationTreeFocus(scope: NavigationTreeFocusScope = "app"): {
  focusVersion: number;
  isFocused: boolean;
  isFocusedRef: RefObject<boolean>;
} {
  const navigation = useNavigation();
  const committedPrimaryNavigationDestinationNameRef = useContext(
    CommittedPrimaryNavigationDestinationNameRefContext,
  );
  const readIsFocused = () =>
    (committedPrimaryNavigationDestinationNameRef === null ||
      committedPrimaryNavigationDestinationNameRef.current === "browse") &&
    isNavigationTreeFocused(navigation as NavigationLike);
  const initialIsFocused = readIsFocused();
  const [focusVersion, setFocusVersion] = useState(0);
  const [isFocused, setIsFocused] = useState(initialIsFocused);
  const isFocusedRef = useRef(initialIsFocused);

  useEffect(() => {
    const nav = navigation as NavigationLike;
    let cancelScheduledUpdate: (() => void) | undefined;
    const publishFocus = (nextIsFocused: boolean) => {
      const wasFocused = isFocusedRef.current;
      // The ref is the source of truth for work guards inside a frozen subtree. React Native
      // Screens may freeze the tree before `setIsFocused(false)` commits, but navigation listeners
      // and their scheduled callbacks still run, so this imperative value remains current.
      isFocusedRef.current = nextIsFocused;
      setIsFocused(nextIsFocused);
      if (nextIsFocused && !wasFocused) {
        // This monotonic value survives a frozen false → true state transition being collapsed into
        // a single render. Consumers use it to refresh data once the subtree is foregrounded again.
        setFocusVersion((version) => version + 1);
      }
    };
    const readFocus = () => {
      // The bottom tab's committed route is authoritative. A nested navigator can continue to
      // report `isFocused() === true` while its entire Browse scene is preserved and frozen behind
      // Learn; combining both sources prevents that stale nested value from reopening DB work.
      if (scope === "bottom-tab" && !isBrowseFrontPrimaryNavigationDestination(nav)) {
        // Another bottom tab is in front: hold whatever this route was inside Browse.
        return;
      }
      publishFocus(
        (committedPrimaryNavigationDestinationNameRef === null ||
          committedPrimaryNavigationDestinationNameRef.current === "browse") &&
          isNavigationTreeFocused(nav),
      );
    };
    const updateAfterFocus = () => {
      // React Navigation can emit an ancestor focus/blur event before every navigator in the
      // chain reports its committed `isFocused()` value. Read immediately, then again on the next
      // frame so the full tree is marked focused once every ancestor has committed the transition.
      readFocus();
      cancelScheduledUpdate?.();
      cancelScheduledUpdate = safeRequestAnimationFrame(() => {
        cancelScheduledUpdate = undefined;
        readFocus();
      }, "navigation-tree-focus");
    };
    const blur = () => {
      cancelScheduledUpdate?.();
      cancelScheduledUpdate = undefined;
      if (scope === "bottom-tab" && !isBrowseFrontPrimaryNavigationDestination(nav)) {
        return;
      }
      // A blur event from ANY navigator in the ancestor chain means the full tree is definitely
      // inactive. Publish that synchronously instead of consulting `isFocused()`, which can still
      // report its pre-transition value while the event is being delivered.
      publishFocus(false);
    };
    const unsubscribers: (() => void)[] = [];
    for (
      let current: NavigationLike | undefined = nav;
      current != null;
      current = current.getParent()
    ) {
      unsubscribers.push(
        current.addListener("focus", updateAfterFocus),
        current.addListener("blur", blur),
      );
    }
    updateAfterFocus();
    return () => {
      cancelScheduledUpdate?.();
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [committedPrimaryNavigationDestinationNameRef, navigation, scope]);

  return { focusVersion, isFocused, isFocusedRef };
}

export function useIsNavigationTreeFocused(scope: NavigationTreeFocusScope = "app"): boolean {
  return useNavigationTreeFocus(scope).isFocused;
}
